import React, {
  Context,
  createContext,
  createRef,
  FC,
  PropsWithChildren,
  RefObject,
  useContext,
  useMemo,
  useState,
} from 'react'

interface InputEl {
  focus: () => void
  blur: () => void
}
export interface SearchBarContextData<InputElement extends InputEl> {
  query: string
  focused: boolean
  updateQuery: (q: string) => void
  searchQuery: string
  setFocusedState: (focused: boolean) => void
  inputRef: RefObject<InputElement | null>
}

export interface SearchBarProviderProps<InputElement extends InputEl> {
  transformQuery?: (query: string) => string
  inputRef: RefObject<InputElement | null>
}
export const SearchBarContext = createContext<SearchBarContextData<any>>({
  query: '',
  updateQuery: () => {},
  setFocusedState: () => {},
  focused: false,
  searchQuery: '',
  inputRef: createRef<HTMLInputElement | null>(),
})
export const useSearchBarState = <InputElement extends InputEl>(
  inputRef: RefObject<InputElement | null>,
  transformQuery?: (query: string) => string,
): SearchBarContextData<InputElement> => {
  const [focused, setFocused] = useState(false)
  const [query, setQuery] = useState('')
  // const inputRef = useRef<HTMLInputElement>(null)
  const searchQuery = useMemo(
    () => (transformQuery ? transformQuery(query) : query),
    [query, transformQuery],
  )
  return {
    query,
    focused,
    inputRef,
    setFocusedState: setFocused,
    searchQuery,
    updateQuery: s => setQuery(s),
  }
}

export const BaseSearchBarProvider: FC<
  PropsWithChildren<{
    transformQuery?: (query: string) => string
    inputRef: RefObject<InputEl | null>
  }>
> = ({ children, transformQuery, inputRef }) => (
  <SearchBarContext.Provider value={useSearchBarState(inputRef, transformQuery)}>
    {children}
  </SearchBarContext.Provider>
)

export const useSearchBase = <InputElement extends InputEl>() =>
  useContext(SearchBarContext as Context<SearchBarContextData<InputElement>>)
